import { useCallback, useEffect, useState } from 'react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'

export function useVisibleSecondaryFilters<TFilters, TCtx>({
  secondaryKeys,
  filters,
  definitionsByKey,
  ctx,
}: {
  secondaryKeys: string[]
  filters: TFilters
  definitionsByKey: Map<string, FilterDefinition<TFilters, TCtx>>
  ctx: TCtx
}) {
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    setVisibleKeys((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const key of secondaryKeys) {
        const def = definitionsByKey.get(key)
        if (!def) continue
        if (def.isNonDefault(filters, ctx) && !next.has(key)) {
          next.add(key)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [filters, secondaryKeys, definitionsByKey, ctx])

  const add = useCallback((key: string) => {
    setVisibleKeys((prev) => new Set(prev).add(key))
  }, [])

  const remove = useCallback(
    (key: string, onFiltersChange: (next: TFilters) => void, current: TFilters) => {
      const def = definitionsByKey.get(key)
      if (!def) return
      onFiltersChange(def.reset(current))
      setVisibleKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    },
    [definitionsByKey],
  )

  const clearVisible = useCallback(() => {
    setVisibleKeys(new Set())
  }, [])

  return { visibleKeys, add, remove, clearVisible }
}
